import { videos, heroIds, heroBackdrops } from "@/mocks/videos";
import type { Video, VideoType } from "@/types/video";

const allVideos = videos as Video[];

export const PAGE_SIZE = 12;

export const REGION_OPTIONS = [
  "全部",
  "中国大陆",
  "美国",
  "韩国",
  "日本",
  "中国香港",
  "英国",
  "法国",
  "德国",
  "泰国",
];

export const YEAR_OPTIONS = ["全部", "2024", "2023", "2022"];

export const GENRE_OPTIONS: Record<VideoType, string[]> = {
  movie: ["全部", "动作", "喜剧", "爱情", "科幻", "悬疑", "犯罪", "古装", "战争", "奇幻", "动画", "冒险", "剧情"],
  tv: ["全部", "悬疑", "爱情", "古装", "科幻", "剧情", "都市", "谍战", "历史", "喜剧", "励志", "惊悚"],
  variety: ["全部", "真人秀", "美食", "音乐", "竞技", "脱口秀", "旅行", "表演", "舞蹈", "亲子", "恋综", "纪实"],
};

export type SortKey = "updated" | "hot" | "rating";

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "updated", label: "按更新时间" },
  { key: "hot", label: "按热门程度" },
  { key: "rating", label: "按评分" },
];

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  sort: SortKey;
  keyword: string;
}

export const DEFAULT_FILTER: FilterState = {
  region: "全部",
  year: "全部",
  genre: "全部",
  sort: "updated",
  keyword: "",
};

export function getAllVideos(): Video[] {
  return allVideos;
}

export function getVideoById(id: string): Video | undefined {
  return allVideos.find((item) => item.id === id);
}

export function getVideosByType(type: VideoType): Video[] {
  return allVideos.filter((item) => item.type === type);
}

export function getHeroVideos(): { video: Video; backdrop: string }[] {
  return heroIds
    .map((id, index) => {
      const video = allVideos.find((item) => item.id === id);
      if (!video) return null;
      return { video, backdrop: heroBackdrops[index] ?? video.poster };
    })
    .filter((item): item is { video: Video; backdrop: string } => item !== null);
}

function matchesKeyword(video: Video, keyword: string): boolean {
  if (!keyword) return true;
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  return (
    video.title.toLowerCase().includes(kw) ||
    video.region.toLowerCase().includes(kw) ||
    video.genres.some((g) => g.toLowerCase().includes(kw)) ||
    video.tags.some((t) => t.toLowerCase().includes(kw))
  );
}

export function applyFilters(list: Video[], filter: FilterState): Video[] {
  const filtered = list.filter((video) => {
    if (filter.region !== "全部" && video.region !== filter.region) return false;
    if (filter.year !== "全部" && String(video.year) !== filter.year) return false;
    if (filter.genre !== "全部" && !video.genres.includes(filter.genre)) return false;
    if (!matchesKeyword(video, filter.keyword)) return false;
    return true;
  });

  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (filter.sort === "hot") return b.hot - a.hot;
    if (filter.sort === "rating") return b.rating - a.rating;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
  return sorted;
}

export function searchAll(keyword: string): Video[] {
  return allVideos.filter((video) => matchesKeyword(video, keyword));
}

export function getRelated(video: Video, limit = 5): Video[] {
  const sameType = allVideos.filter((item) => item.type === video.type && item.id !== video.id);
  const scored = sameType
    .map((item) => {
      const overlap = item.genres.filter((g) => video.genres.includes(g)).length;
      return { item, score: overlap * 100 + item.hot / 100 };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);

  if (scored.length >= limit) return scored.slice(0, limit);
  const others = allVideos.filter((item) => item.type !== video.type).sort((a, b) => b.hot - a.hot);
  return [...scored, ...others].slice(0, limit);
}

export function getHotByType(type: VideoType, limit = 4): Video[] {
  return getVideosByType(type)
    .slice()
    .sort((a, b) => b.hot - a.hot)
    .slice(0, limit);
}

export function getLatestVideos(limit = 6): Video[] {
  return allVideos
    .slice()
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export function formatHot(hot: number): string {
  if (hot >= 10000) return `${(hot / 10000).toFixed(1)}万`;
  return String(hot);
}